import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import LoadingSpinner from './components/common/LoadingSpinner';
import ReloadPrompt from './components/common/ReloadPrompt';
import { AppRoutes } from './routes';
import { AuthProvider } from './store/AuthContext';
import { DirectionProvider } from './store/DirectionContext';
import { NetworkProvider } from './store/NetworkContext';
import { ThemeProvider } from './store/ThemeContext';
import { storePublicKey } from './utils/documentVerification';
import { getApiBaseUrl } from './config/env';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: 'always', // Allow queries to run offline (we handle caching ourselves)
    },
    mutations: {
      networkMode: 'always', // Allow mutations to run offline (we queue them in IndexedDB)
    },
  },
});

function App() {
  // Sync Ed25519 public key from backend for offline QR code verification
  useEffect(() => {
    const syncPublicKey = async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/documents/public-key`);
        if (res.ok) {
          const data = await res.json();
          if (data.public_key) {
            storePublicKey(data.public_key);
          }
        }
      } catch {
        // Silent fail - key may already be cached in localStorage
      }
    };
    syncPublicKey();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <DirectionProvider>
          <CssBaseline />
          <ReloadPrompt />
          <NetworkProvider>
            <BrowserRouter>
              <AuthProvider>
                <Suspense fallback={<LoadingSpinner />}>
                  <AppRoutes />
                </Suspense>
              </AuthProvider>
            </BrowserRouter>
          </NetworkProvider>
        </DirectionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
